import React, { useMemo } from "react";

const calculateStats = (matches, division) => {
  const stats = {};

  matches
    .filter((match) => {
      if (!match.result || match.phase === "elimination") return false;

      // Include matches from the same division OR crosspool matches involving this division
      if (match.division === division) return true;

      // For crosspool matches, include if either team is from this division
      if (match.phase === "crosspool") {
        return match.team1?.division === division || match.team2?.division === division;
      }

      return false;
    })
    .forEach((match) => {
      const { team1, team2, result, team1Name, team2Name } = match;

      const team1Display = team1?.players
        ? team1.players.map((p) => p.name).join(" & ")
        : team1Name || "BYE";
      const team2Display = team2?.players
        ? team2.players.map((p) => p.name).join(" & ")
        : team2Name || "BYE";

      // For crosspool matches, only count stats for teams from the target division
      const isTeam1FromDivision = match.phase === "crosspool" ? match.team1?.division === division : true;
      const isTeam2FromDivision = match.phase === "crosspool" ? match.team2?.division === division : true;

      if (isTeam1FromDivision) {
        if (!stats[team1Display])
          stats[team1Display] = { team: team1Display, wins: 0, scored: 0, conceded: 0 };

        const t1Score = result.team1 ?? 0;
        const t2Score = result.team2 ?? 0;

        stats[team1Display].scored += t1Score;
        stats[team1Display].conceded += t2Score;

        if (t1Score > t2Score) stats[team1Display].wins += 1;
      }

      if (isTeam2FromDivision) {
        if (!stats[team2Display])
          stats[team2Display] = { team: team2Display, wins: 0, scored: 0, conceded: 0 };

        const t1Score = result.team1 ?? 0;
        const t2Score = result.team2 ?? 0;

        stats[team2Display].scored += t2Score;
        stats[team2Display].conceded += t1Score;

        if (t2Score > t1Score) stats[team2Display].wins += 1;
      }
    });

  return Object.values(stats).map((stat) => ({
    ...stat,
    difference: stat.scored - stat.conceded,
  }));
};



const StatsTable = ({ matches, teams, tournament }) => {
  const availableDivisions = useMemo(() => {
    return Object.keys(teams || {}).sort((a, b) => {
      if (a === "division1") return -1;
      if (b === "division1") return 1;
      return a.localeCompare(b);
    });
  }, [teams]);

  const hasCrosspoolMatches = useMemo(() =>
    matches?.some((m) => m.phase === "crosspool"), [matches]
  );

  const divisionStats = useMemo(() => {
    return availableDivisions.reduce((acc, division) => {
      const stats = calculateStats(matches, division);
      stats.sort((a, b) => {
        if (b.wins !== a.wins) return b.wins - a.wins;
        return b.difference - a.difference;
      });
      acc[division] = stats;
      return acc;
    }, {});
  }, [matches, availableDivisions]);

  const hasAnyStats = Object.values(divisionStats).some((stats) => stats.length > 0);

  if (!hasAnyStats) return null;

  return (
    <div className="border-t border-slate-800 pt-4 w-full">
      <h4 className="text-lg font-semibold text-slate-50 mb-2">Classement</h4>
      <div className="w-full flex flex-wrap gap-4 justify-center">
      {availableDivisions.map((division) => {
        if (!divisionStats[division]?.length) return null;

        return (
          <div key={division} className="w-full md:w-[48%]">
            <h3 className="text-sm font-bold mb-1">
              {!tournament?.makeDivisions && hasCrosspoolMatches
                ? (division === "division1" ? "Groupe 1" : "Groupe 2")
                : (division === "division1" ? "Division 1" : "Division 2")
              }
            </h3>
            <table className="w-full text-xs lg:text-sm bg-slate-800 text-slate-50 rounded overflow-hidden">
              <thead>
                <tr className="text-amber-400">
                  <th className="px-1 py-1 text-center">#</th>
                  <th className="px-1 py-1 text-left">Équipe</th>
                  <th className="px-1 py-1 text-center">V</th>
                  <th className="px-1 py-1 text-center">PM</th>
                  <th className="px-1 py-1 text-center">PE</th>
                  <th className="px-1 py-1 text-center">+/-</th>
                </tr>
              </thead>
              <tbody>
                {divisionStats[division].map((team, idx) => (
                  <tr key={team.team} className="border-t border-slate-700">
                    <td className="px-1 py-0.5 text-center font-bold text-amber-400">
                      {idx + 1}
                    </td>
                    <td className="px-1 py-0.5 truncate max-w-[140px]">
                      {team.team}
                    </td>
                    <td className="px-1 py-0.5 text-center">
                      {team.wins}
                    </td>
                    <td className="px-1 py-0.5 text-center">
                      {team.scored}
                    </td>
                    <td className="px-1 py-0.5 text-center">
                      {team.conceded}
                    </td>
                    <td className="px-1 py-0.5 text-center font-bold text-emerald-400">
                      {team.difference > 0 ? "+" : ""}
                      {team.difference}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        );
      })}
      </div>
    </div>
  );
};

export default StatsTable;
